/* eslint-disable no-empty */
import { UsersWithProfilePicsExtractChannel } from '@tableau/omnibox-common/lib/src/channelDefinitions/UsersWithProfilePicsExtractChannel'
import { SiteUrls } from '@tableau/shared-urls';
import axios, { AxiosRequestConfig } from 'axios';
import { getSessionManagementServiceInstance, SessionManagementService } from './SessionManagementService';
import { ContentDefinitions } from '../models/ContentDefinitions';
import { ContentSuggestionData, ContentSuggestionDataItem, ContentType, DraftDataItem, DraftReferenceType, DraftsResponse, UserResponse, WorkbookResponse } from '../models/Types';
import { HostComponentOptions, OmniboxConfigurationService } from '../OmniboxConfigurationService';
import { FeatureFlag, FeatureFlagService } from './FeatureFlagService';

export type ownerInCRASSData = { content: { ownerId: string, ownerName: string } };

export class SearchService {
    public static readonly instance = new SearchService();
    private server: SessionManagementService;
    private draftDataItemsCache: DraftDataItem[];

    public initialize() {
        this.server = getSessionManagementServiceInstance();
    }

    public get Server(): SessionManagementService {
        return this.server;
    }

    public usersWithProfilePictures(ids: string[]) {
        const channel = new UsersWithProfilePicsExtractChannel();
        return channel.call(ids).data?.usersWithProfilePics;
    }

    public async getContentSuggestions(searchTerm: string): Promise<ContentSuggestionDataItem[]> {
        const availabeContentTypeKeys = OmniboxConfigurationService.instance.configuration.requiredContentTypes.map(contentType => {
            return ContentDefinitions[contentType].shouldBeAvailable() ? contentType : null;
        }).filter(item => item !== null);

        const parameters = [`terms=${searchTerm}`];
        parameters.push(`filter=type:in:[${availabeContentTypeKeys.join(',')}]`);

        const response = await axios.get(OmniboxConfigurationService.instance.configuration.apiHost + `/content-exploration/suggestions?${parameters.join('&')}`, this.getAxiosConfig());
        const data = response.data as ContentSuggestionData;

        this.deDupContentSuggestionOnDataSource(data?.hits?.items);

        if (OmniboxConfigurationService.instance.configuration.hostComponent !== HostComponentOptions.ConnectTo) {
            const draftDataItems = await this.getDrafts();
            const result = data?.hits?.items
            this.processDraftData(result, draftDataItems);
        }

        return data?.hits?.items;
    }

    public async getEmptyContentSuggestions(): Promise<ContentSuggestionDataItem[]> {
        const availabeContentTypeKeys = OmniboxConfigurationService.instance.configuration.requiredContentTypes.map(contentType => {
            return ContentDefinitions[contentType].shouldBeAvailable() ? contentType : null;
        }).filter(item => item !== null);

        const parameters: string[] = [];
        parameters.push('page=0');
        if (OmniboxConfigurationService.instance.configuration.emptyStateContentSuggestionFetchLimit) {
            parameters.push(`limit=${OmniboxConfigurationService.instance.configuration.emptyStateContentSuggestionFetchLimit}`);
        } else {
            parameters.push('limit=20');
        }
        parameters.push(`filter=type:in:[${availabeContentTypeKeys.join(',')}]`);

        const response = await axios.get(OmniboxConfigurationService.instance.configuration.apiHost + `/content-exploration/search?${parameters.join('&')}`, this.getAxiosConfig());
        const data = response.data as ContentSuggestionData;
        this.deDupContentSuggestionOnDataSource(data?.hits?.items);
        const draftDataItems = await this.getDrafts();
        const result = data?.hits?.items.filter(contentSuggestionItem => draftDataItems.slice(0, OmniboxConfigurationService.instance.configuration.jumpBackInMaxCount || 0).filter(draftDataItem => draftDataItem.luid === contentSuggestionItem.content.luid).length === 0);
        this.processDraftData(result, draftDataItems);
        return result;
    }

    public async getRelatedOwners(): Promise<ownerInCRASSData[]> {
        const availabeContentTypeKeys = OmniboxConfigurationService.instance.configuration.requiredContentTypes.map(contentType => {
            return ContentDefinitions[contentType].shouldBeAvailable() ? contentType : null;
        }).filter(item => item !== null);

        const parameters = [];
        parameters.push(`filter=type:in:[${availabeContentTypeKeys.join(',')}]`);
        parameters.push('limit=50');

        const response = await axios.get(OmniboxConfigurationService.instance.configuration.apiHost + `/content-exploration/search?${parameters.join('&')}`, this.getAxiosConfig());
        return response.data?.hits?.items || [];
    }

    public getDrafts(): Promise<DraftDataItem[]> {
        return new Promise<DraftDataItem[]>((resolve) => {
            if (this.draftDataItemsCache) {
                resolve(this.draftDataItemsCache);
            } else {
                const resolveWithDraftDataItems = (draftDataItems: DraftDataItem[]) => {
                    this.draftDataItemsCache = draftDataItems;
                    resolve(draftDataItems);
                };

                const urls = new SiteUrls({ siteUrlName: SessionManagementService.instance.sessionSiteUrlName(), queryString: window.location.search });

                const siteLuid = SessionManagementService.instance.sessionSiteLuid();
                axios.get(OmniboxConfigurationService.instance.configuration.apiHost + `/api/3.9/sites/${siteLuid}/workbookdrafts`, null).then(draftsResponse => {
                    if (draftsResponse) {
                        const map: { [id: string]: DraftDataItem } = {};
                        const workbookResponsePromises: Promise<void>[] = [];
                        const draftDataItems = (draftsResponse as DraftsResponse).data
                            .sort((a, b,) => b.createdAt - a.createdAt)
                            .map(item => {
                                const draftDataItem: DraftDataItem = {
                                    luid: item.draftReferenceId || item.parentContentLuid,
                                    containerLuid: item.containerLuid,
                                    containerType: item.containerType,
                                    title: item.name,
                                    ownerName: '',
                                    ownerId: item.userLuid,
                                    url: '',
                                    type: item.draftReferenceType
                                };
                                map[draftDataItem.luid] = draftDataItem;
                                return draftDataItem;
                            });

                        draftDataItems.forEach(draftDataItem => {
                            workbookResponsePromises.push(new Promise<void>((resolve) => {
                                if (!draftDataItem.type || draftDataItem.type === DraftReferenceType.PublishedWorkbook) {
                                    this.getWorkbookByLuid(siteLuid, draftDataItem.luid).then(workbookResponse => {
                                        if (workbookResponse) {
                                            const draftDataItem = map[workbookResponse.data.workbook.id];
                                            if (draftDataItem) {
                                                draftDataItem.title = workbookResponse.data.workbook.name;
                                                draftDataItem.url = workbookResponse.data.workbook.webpageUrl;
                                                draftDataItem.ownerId = workbookResponse.data.workbook.owner.id;
                                                this.getUserByLuid(siteLuid, workbookResponse.data.workbook.owner.id).then(userResponse => {
                                                    draftDataItem.ownerName = userResponse ? userResponse.data.user.fullName : '';
                                                    resolve();
                                                });
                                            } else {
                                                resolve(); // When workbook request fails, still resolve but without pushing any draft data item into the result. 
                                            }
                                        } else {
                                            resolve(); // When workbook request fails, still resolve but without pushing any draft data item into the result.
                                        }
                                    });
                                } else {
                                    if (draftDataItem) {
                                        draftDataItem.url = urls.unpublishedWorkbookUrl(draftDataItem.luid, draftDataItem.containerType === 'PROJECT' ? draftDataItem.containerLuid : undefined);
                                        this.getUserByLuid(siteLuid, draftDataItem.ownerId).then(userResponse => {
                                            draftDataItem.ownerName = userResponse ? userResponse.data.user.fullName : '';
                                            resolve();
                                        });
                                    } else {
                                        resolve(); // When workbook request fails, still resolve but without pushing any draft data item into the result. 
                                    }
                                }
                            }));
                        });
                        Promise.all(workbookResponsePromises).then(() => {
                            resolveWithDraftDataItems(draftDataItems.filter(item => item.url));
                        });
                    }
                }, () => {
                    resolveWithDraftDataItems([]); // When draft request fails, still resolve the result but with an empty array.
                });
            }
        });
    }

    public async personalizationRefresh() {
        try {
            await axios.post(OmniboxConfigurationService.instance.configuration.apiHost + '/content-exploration/personalization/refresh', null, this.getAxiosConfig());
        } catch {
        }
    }

    private getAxiosConfig(): AxiosRequestConfig {
        const config: AxiosRequestConfig = { headers: {} };

        if (OmniboxConfigurationService.instance.configuration.xsrfToken) {
            config.headers['X-XSRF-TOKEN'] = OmniboxConfigurationService.instance.configuration.xsrfToken;
            config.headers['WithCredentials'] = true;
        }

        return config;
    }

    private async getWorkbookByLuid(siteLuid: string, workbookLuid: string): Promise<WorkbookResponse> {
        try {
            return (await axios.get(OmniboxConfigurationService.instance.configuration.apiHost + `/api/3.16/sites/${siteLuid}/workbooks/${workbookLuid}`, null));
        } catch (e) {
        }
    }

    private async getUserByLuid(siteLuid: string, userLuid: string): Promise<UserResponse> {
        try {
            return (await axios.get(OmniboxConfigurationService.instance.configuration.apiHost + `/api/3.16/sites/${siteLuid}/users/${userLuid}`, null));
        } catch (e) {
        }
    }

    private processDraftData(contentSuggestionItems: ContentSuggestionDataItem[], draftDataItems: DraftDataItem[]) {
        if (contentSuggestionItems) {
            contentSuggestionItems.forEach(item => {
                item.content.hasDraft = FeatureFlagService.instance.getValue(FeatureFlag.searchDraftsEnabled) && draftDataItems.filter(draft => draft.luid === item.content.luid).length > 0;
            });
        }
    }

    private deDupContentSuggestionOnDataSource(contentSuggestionItems?: ContentSuggestionDataItem[]) {
        // The following processing of data source related search results is due to Defect W-13513283: Duplicate results in search popup box
        // Based on the current server side implementation, the data source search results are actually about data connections. One data source may have multiple data connections.
        // When the user searches data source related keywords, multiple data connections may be returned on rendered on the UI with the parent data source information.
        // It looks like the UI is rendering duplicated data source results.
        // Based on engineering cost and impact considering, it's decided that the client side will dedup the data source search result to avoid duplicated information being rendered on the UI.
        if (contentSuggestionItems) {
            const dataSourceIDMap: { [id: string]: boolean } = {};
            for (let i = 0; i < contentSuggestionItems.length; i++) {
                const currentItem = contentSuggestionItems[i];
                if (currentItem.content && currentItem.content.type===ContentType.unifieddatasource && currentItem.content.datasourceId) {
                    const dataSourceId = currentItem.content.datasourceId;
                    if (dataSourceIDMap[dataSourceId]) {
                        contentSuggestionItems.splice(i, 1);
                        i--;
                    }
                    dataSourceIDMap[dataSourceId] = true;
                }
            }
        }
    }
}